import {
  useMergedShopTests,
  AllTestKitInfo,
  useShopifyMultipass,
} from '@viome/viome-base/src/usecase/Shop/ShopQuery';
import {
  Card,
  CardDescription,
  CardTitle,
} from '@/components/design-system/Card';
import { Button } from '@/components/design-system/Button';
import { getFormattedDate } from '@/utils/date/getFormattedDate';
import { ErrorLoadingPage } from '@/components/screens/ErrorHandling/ErrorLoadingPage';
import { Skeleton } from '@/components/design-system/Skeleton';
import { useToast } from '@/hooks/use-toast';
import { useState } from 'react';
import { EventTracker } from '@/utils';
import { analyticsEvent } from '@utils/analyticsEvent.ts';

const ShopItemSkeleton = () => (
  <Card className="overflow-hidden mb-6">
    <div className="px-5 pt-6">
      <div className="pb-6 flex flex-col-reverse md:flex-row items-center md:items-start">
        <Skeleton className="h-8 w-40 rounded" />
        <Skeleton className="h-6 w-48 mt-1 sm:mt-0 ml-auto" />
      </div>

      <div className="flex flex-col-reverse md:flex-row justify-between items-center">
        <div>
          <Skeleton className="h-8 w-60 mb-4" />
          <Skeleton className="h-8 w-24 mb-4" />
          <Skeleton className="h-10 w-40" />
        </div>
        <Skeleton className="w-48 h-40 mt-4 md:mt-0" />
      </div>
    </div>

    <div className="px-5 py-6">
      <Skeleton className="h-24 w-full mb-6" />
      <div className="flex gap-4 w-full">
        <Skeleton className="flex-1 h-14 rounded-3xl" />
      </div>
    </div>
  </Card>
);

const PriceCalculationDisplay = ({
  testData: product,
}: {
  testData: AllTestKitInfo;
}) => {
  const discountPrice = product?.discountAmount ?? 0;
  const discountPercentage = product?.discountPercentage ?? 0;

  // Calculate original price and check if discount exists
  const hasDiscount = discountPrice > 0 || discountPercentage > 0;

  const originalPrice = product?.current_price;
  let discountedPrice = originalPrice;

  if (hasDiscount) {
    if (discountPrice > 0) {
      // Priority 1: Use discount amount - SUBTRACT the discount
      discountedPrice = Math.max(0, originalPrice - discountPrice);
    } else if (discountPercentage > 0) {
      // Priority 2: Use discount percentage
      discountedPrice = originalPrice * (1 - discountPercentage / 100);
    }
  }

  const finalDiscountedPrice = Math.round(discountedPrice * 100) / 100;

  return (
    <div className="flex items-center space-x-2 mb-6 justify-center md:justify-start">
      {hasDiscount && (
        <span className="typography-h3 text-secondary-foreground line-through">
          ${originalPrice.toFixed(2)}
        </span>
      )}
      <span className="typography-h3 text-success">
        ${finalDiscountedPrice.toFixed(2)}
      </span>
    </div>
  );
};

export const ShopTestsPage = () => {
  const { data, isLoading, isError } = useMergedShopTests();
  const { mutate: generateMultipassUrl, isPending: isMultipassLoading } =
    useShopifyMultipass();
  const { toast } = useToast();
  const [processingTestId, setProcessingTestId] = useState<string | null>(null);

  const handleCtaClick = (test: AllTestKitInfo) => {
    if (!test.testId) {
      toast({
        variant: 'destructive',
        title: 'Error',
        description: 'An unexpected error occurred. Please try again.',
      });
      console.error('Test ID not found for cart operation.');
      return;
    }
    let finalShopifyCartUrl = test?.shopify_cart_url;
    if (!finalShopifyCartUrl) {
      toast({
        variant: 'destructive',
        title: 'Error',
        description: 'Shopify cart URL not found.',
      });
      return;
    }
    if (test?.couponCode) {
      const separator = finalShopifyCartUrl.includes('?') ? '&' : '?';
      finalShopifyCartUrl += `${separator}discount=${test?.couponCode}`;
    }
    setProcessingTestId(test.testId);
    generateMultipassUrl(finalShopifyCartUrl, {
      onSuccess: (multipassData) => {
        setProcessingTestId(null);
        if (multipassData) {
          window.open(multipassData, '_blank');
        } else {
          toast({
            variant: 'destructive',
            title: 'Error',
            description: 'Could not retrieve checkout URL. Please try again.',
          });
        }
      },
      onError: (error: Error) => {
        setProcessingTestId(null);
        toast({
          variant: 'destructive',
          title: 'Error',
          description:
            error.message || 'Could not proceed to checkout. Please try again.',
        });
      },
    });
    if (test?.testId === '32624267690020') {
      EventTracker.trackEvent('shop', analyticsEvent.SHOP_TESTS_GI_ORDERCTA);
    } else if (test?.testId === '40443528183844') {
      EventTracker.trackEvent('shop', analyticsEvent.SHOP_TESTS_OHI_ORDERCTA);
    } else if (test?.testId === '39953050566692') {
      EventTracker.trackEvent('shop', analyticsEvent.SHOP_TESTS_FBI_ORDERCTA);
    }
  };

  if (isLoading) {
    return (
      <div className="space-y-8">
        <ShopItemSkeleton />
        <ShopItemSkeleton />
      </div>
    );
  }

  if (isError || !data?.length) {
    return (
      <ErrorLoadingPage
        variant="light"
        errorType="error"
        hideBackground
        hideNavBar
      />
    );
  }

  return (
    <div className="space-y-8">
      {data?.map((test) => (
        <Card key={test.testId} className="overflow-hidden mb-6">
          <div className="px-5 pt-6">
            {(test?.productMessage || test?.last_test_date) && (
              <div className="pb-6 flex flex-col-reverse md:flex-row items-center md:items-start">
                {test.productMessage && (
                  <span className="typography-btn-medium text-tertiary bg-background-tertiary p-2 rounded w-fit mx-auto sm:mx-0">
                    {test.productMessage}
                  </span>
                )}
                {test.last_test_date && (
                  <span className="typography-btn-medium text-tertiary mt-1 sm:mt-0 py-2 pl-5">
                    Last tested:{' '}
                    {getFormattedDate(test.last_test_date, 'MMM D, YYYY')}
                  </span>
                )}
              </div>
            )}

            <div className="flex flex-col-reverse md:flex-row justify-between">
              <div>
                <CardTitle className="typography-h2 text-primary mb-4">
                  {test.title}
                </CardTitle>

                <PriceCalculationDisplay testData={test} />

                {test.promo && (
                  <div className="flex flex-nowrap max-w-fit backdrop-blur-[25px]">
                    {test.promo.splitOne && (
                      <div
                        className={`typography-caption text-primary-foreground py-2 px-3 bg-gradient-to-r from-[#57D3BD] to-[#038F76] ${
                          test.promo.splitTwo
                            ? 'rounded-l-full'
                            : 'rounded-full'
                        }`}
                      >
                        {test.promo.splitOne}
                      </div>
                    )}
                    {test.promo.splitTwo && (
                      <div className="typography-btn-medium flex items-center text-background-success py-2 px-3 rounded-r-full bg-gradient-to-r from-[#B1EAE0] to-[#A2E6D7]">
                        {test.promo.splitTwo}
                      </div>
                    )}
                  </div>
                )}
              </div>

              {test.productImageUrl && (
                <div className="flex-shrink-0 mt-4 md:mt-0">
                  <img
                    src={test.productImageUrl}
                    alt={test.title}
                    className="w-auto h-auto max-h-[165px] object-contain"
                  />
                </div>
              )}
            </div>
          </div>

          <div className="px-5 py-6">
            <CardDescription className="typography-body text-tertiary mb-6">
              {test?.description}
            </CardDescription>

            <div className="flex gap-4 w-full">
              {test?.cta && (
                <Button
                  style={{ backgroundColor: test?.cta?.ctaColor }}
                  className="flex-1 h-14 rounded-3xl"
                  disabled={isMultipassLoading}
                  onClick={() => handleCtaClick(test)}
                >
                  {isMultipassLoading && processingTestId === test.testId
                    ? 'Processing...'
                    : test?.last_test_date
                      ? 'Retest now'
                      : test.cta?.ctaText}
                </Button>
              )}
            </div>
          </div>
        </Card>
      ))}
    </div>
  );
};
